import { Link } from "react-router-dom";

interface ScreenshotBlockProps {
  src: string;
  alt: string;
  label: string;
}

const ScreenshotBlock = ({ src, alt, label }: ScreenshotBlockProps) => (
  <div className="rounded-xl overflow-hidden shadow-2xl border border-gray-200 bg-gray-50">
    <div className="bg-gray-800 px-4 py-3 flex items-center space-x-2">
      <div className="w-3 h-3 bg-red-500 rounded-full" />
      <div className="w-3 h-3 bg-yellow-500 rounded-full" />
      <div className="w-3 h-3 bg-green-500 rounded-full" />
      <span className="text-gray-400 text-xs ml-2 font-mono">
        sprdgen.avtechai.com
      </span>
    </div>
    <div className="aspect-video overflow-hidden">
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover block"
        onError={(e) => {
          const target = e.currentTarget;
          target.style.display = "none";
          const placeholder = target.nextElementSibling as HTMLElement;
          if (placeholder) placeholder.style.display = "flex";
        }}
      />
    </div>
    <div
      className="hidden flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 min-h-64 p-8 text-center"
      style={{ display: "none" }}
    >
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
        <svg
          className="w-8 h-8 text-blue-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
      <p className="text-blue-600 font-semibold text-sm">{label}</p>
      <p className="text-gray-400 text-xs mt-1">Screenshot coming soon</p>
    </div>
  </div>
);

const CheckIcon = () => (
  <svg
    className="w-5 h-5 text-green-500 flex-shrink-0"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export const SpreadGeniusPage = () => {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center bg-white/10 backdrop-blur border border-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <svg
                  className="w-4 h-4 mr-2 text-yellow-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Options Trading Analytics Platform
              </div>

              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Spread <span className="text-yellow-300">Genius</span>
              </h1>

              <p className="text-xl text-blue-100 mb-4 leading-relaxed">
                Upload your broker statements, automatically identify every
                spread strategy you've traded, and visualize your performance
                with institutional-grade analytics.
              </p>
              <p className="text-lg text-blue-200 mb-8">
                Premium members unlock live portfolio data, real-time option
                chains, and AI-powered trade signals — all powered by the{" "}
                <strong className="text-white">Charles Schwab API</strong>.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://sprdgen.avtechai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-blue-700 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors inline-flex items-center justify-center"
                >
                  Launch App — Free
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
                <Link
                  to="/schwab"
                  className="border-2 border-white/60 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center"
                >
                  Schwab API Integration
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </Link>
              </div>
            </div>

            <div className="lg:pl-4">
              <ScreenshotBlock
                src="/screenshots/transaction-analysis.webp"
                alt="Spread Genius transaction analysis dashboard"
                label="Transaction Analysis Dashboard"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Supported Brokers */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
            <span className="text-gray-500 text-sm font-medium mr-2">
              Supported brokers:
            </span>
            <span className="bg-white border border-gray-200 px-5 py-2 rounded-full text-sm font-semibold text-gray-700 shadow-sm">
              Charles Schwab
            </span>
            <span className="bg-white border border-gray-200 px-5 py-2 rounded-full text-sm font-semibold text-gray-700 shadow-sm">
              Fidelity
            </span>
            <span className="bg-blue-50 border border-blue-200 px-5 py-2 rounded-full text-sm font-semibold text-blue-700 shadow-sm">
              + Live Schwab API (Premium)
            </span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From raw broker export to actionable analytics in minutes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Upload Your CSV
              </h3>
              <p className="text-gray-600">
                Export your transaction history from Schwab or Fidelity and
                upload it directly — no account linking required for the free
                tier.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Strategies Auto-Detected
              </h3>
              <p className="text-gray-600">
                Our engine scans your trades and automatically classifies every
                spread: covered calls, cash-secured puts, vertical spreads, iron
                condors, strangles, and more.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold shadow-lg">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Go Live with Schwab
              </h3>
              <p className="text-gray-600">
                Connect your Schwab account to unlock real-time positions, live
                option chains, the covered-call scanner, Risk Radar, and
                high-conviction trade signals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Free Tier Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Free — No Credit Card Required
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Powerful CSV Analytics, Free Forever
              </h2>

              <p className="text-lg text-gray-600 mb-8">
                Upload your broker export and get instant strategy analysis.
                Understand your trading patterns, P&amp;L attribution, and win
                rates across every strategy type.
              </p>

              <div className="space-y-4">
                {[
                  "Schwab and Fidelity CSV import",
                  "Automatic strategy detection (20+ spread types)",
                  "P&L breakdown by strategy, ticker, and time period",
                  "Win rate and average return analytics",
                  "Interactive charts — equity curve, strategy mix",
                  "Premium / discount analysis on entry and exit",
                  "Export reports to PDF",
                ].map((feature) => (
                  <div key={feature} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <ScreenshotBlock
                src="/screenshots/transaction-analysis-detail.webp"
                alt="Transaction analysis with strategy breakdown"
                label="Strategy Detection — Covered Calls, Spreads, Condors"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Premium Schwab-Powered Features */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-white/10 border border-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <svg
                className="w-4 h-4 mr-2 text-yellow-300"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Premium — Powered by Charles Schwab API
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Live Data. Real-Time Intelligence.
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Connect your Schwab account via secure OAuth and unlock a suite of
              tools that work directly with your live portfolio.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Covered Call Scanner */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-8">
              <div className="w-12 h-12 bg-green-500/20 border border-green-400/30 rounded-xl flex items-center justify-center mb-6">
                <svg
                  className="w-6 h-6 text-green-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Covered Call Scanner
              </h3>
              <p className="text-blue-200 mb-6">
                Scans your live Schwab equity positions to identify covered-call
                opportunities. Filters by delta tier, expiry, and premium yield
                — so you only see the trades worth making.
              </p>
              <ul className="space-y-2 text-sm text-blue-100">
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Real-time option chain from Schwab Market Data API
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Delta-filtered strikes (0.15–0.35 default)
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Annualized yield calculation per contract
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  One-click order preview via Schwab Trader API
                </li>
              </ul>
            </div>

            {/* Risk Radar */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-8">
              <div className="w-12 h-12 bg-red-500/20 border border-red-400/30 rounded-xl flex items-center justify-center mb-6">
                <svg
                  className="w-6 h-6 text-red-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Risk Radar</h3>
              <p className="text-blue-200 mb-6">
                A visual risk dashboard across your entire live portfolio.
                Identifies concentration risk, uncovered positions, and expiry
                clusters so you can manage risk proactively.
              </p>
              <ul className="space-y-2 text-sm text-blue-100">
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Live positions pulled from Schwab Trader API
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Sector and ticker concentration heatmap
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Expiry calendar with net delta exposure
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Margin utilization monitoring
                </li>
              </ul>
            </div>

            {/* Cash-Secured Put Scoring */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-8">
              <div className="w-12 h-12 bg-purple-500/20 border border-purple-400/30 rounded-xl flex items-center justify-center mb-6">
                <svg
                  className="w-6 h-6 text-purple-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Cash-Secured Put Scoring
              </h3>
              <p className="text-blue-200 mb-6">
                Algorithmic scoring model that ranks cash-secured put
                opportunities across your watchlist. Combines IV rank, support
                levels, and earnings calendar to surface high-quality setups.
              </p>
              <ul className="space-y-2 text-sm text-blue-100">
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  IV Rank and IV Percentile from live market data
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Earnings date awareness (auto-avoid)
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Support/resistance level scoring
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Max profit, max loss, and breakeven display
                </li>
              </ul>
            </div>

            {/* High-Conviction Signals */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-8">
              <div className="w-12 h-12 bg-yellow-500/20 border border-yellow-400/30 rounded-xl flex items-center justify-center mb-6">
                <svg
                  className="w-6 h-6 text-yellow-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                High-Conviction Signals
              </h3>
              <p className="text-blue-200 mb-6">
                Fibonacci retracement–based entry signals across your personal
                watchlist. Identifies high-probability confluences where price,
                momentum, and options flow align.
              </p>
              <ul className="space-y-2 text-sm text-blue-100">
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Fibonacci 38.2%, 50%, 61.8% retracement levels
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Multi-timeframe confluence detection
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Personalized watchlist from your Schwab account
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon />
                  Monday morning signal scan
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>
            <p className="text-xl text-gray-600">
              A tour of the premium trading workspace
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Risk Radar Dashboard
              </p>
              <ScreenshotBlock
                src="/screenshots/risk-radar.webp"
                alt="Risk Radar portfolio risk dashboard"
                label="Risk Radar"
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Covered Call Scanner
              </p>
              <ScreenshotBlock
                src="/screenshots/covered-calls.webp"
                alt="Covered call scanner with Schwab live data"
                label="Covered Call Scanner"
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Cash-Secured Put Scoring
              </p>
              <ScreenshotBlock
                src="/screenshots/csp-scoring.webp"
                alt="Cash-secured put scoring and ranking"
                label="Cash-Secured Put Scoring"
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                High-Conviction Signals
              </p>
              <ScreenshotBlock
                src="/screenshots/high-conviction.webp"
                alt="High-conviction Fibonacci-based signals"
                label="High-Conviction Signals"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Monday Scan */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center bg-white/20 text-white px-3 py-1.5 rounded-full text-sm font-medium mb-4">
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Every Monday, 9 AM ET
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Automated Weekly Scan
                </h3>
                <p className="text-blue-100 text-lg">
                  Every Monday morning before market open, Spread Genius
                  automatically scans your Schwab portfolio and watchlist,
                  generating a fresh set of covered-call and CSP recommendations
                  — ready for the week ahead.
                </p>
              </div>
              <div className="bg-white/10 border border-white/20 rounded-xl p-6">
                <div className="space-y-3 text-sm">
                  {[
                    "Covered-call candidates ranked by yield",
                    "Cash-secured put setups with IV Rank filter",
                    "High-conviction signals from your watchlist",
                    "Earnings calendar cross-check (auto-excluded)",
                    "Sent to your inbox + available in-app",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-300 rounded-full flex-shrink-0" />
                      <span className="text-blue-100">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Built on a Modern, Serverless Stack
            </h2>
            <p className="text-gray-600">
              Secure, scalable, and built for real-time financial data
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: "React 19", desc: "Frontend UI", color: "blue" },
              { name: "TypeScript", desc: "Type-safe code", color: "blue" },
              {
                name: "AWS Lambda",
                desc: "Serverless backend",
                color: "orange",
              },
              { name: "DynamoDB", desc: "Data storage", color: "orange" },
              {
                name: "Schwab API",
                desc: "Live market data",
                color: "green",
              },
              {
                name: "Cognito",
                desc: "Secure auth",
                color: "purple",
              },
            ].map(({ name, desc, color }) => (
              <div
                key={name}
                className={`bg-white border border-${color}-100 rounded-xl p-4 text-center shadow-sm`}
              >
                <p className="font-bold text-gray-900 text-sm">{name}</p>
                <p className="text-gray-500 text-xs mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Start Analyzing Your Trades Today
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Free for CSV analysis. Upgrade for live Schwab data and AI-powered
            signals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://sprdgen.avtechai.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-blue-500 transition-colors inline-flex items-center justify-center"
            >
              Get Started Free
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </a>
            <Link
              to="/contact"
              className="border-2 border-gray-500 text-gray-300 px-10 py-4 rounded-lg font-bold text-lg hover:border-white hover:text-white transition-colors inline-flex items-center justify-center"
            >
              Request Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
